// ============================================================================
// OBT — Ortak durum ve sunum bileşenleri
// ============================================================================
// Mevcut tasarım sistemi (shadcn/ui + Tailwind) kullanılır; OBT'ye özel yeni
// bir tema veya bağımsız CSS yaklaşımı KURULMAZ.
//
// Erişilebilirlik:
// - Yükleme ve hata durumları `role="status"` / `role="alert"` ile ekran
//   okuyuculara bildirilir.
// - Renk TEK BAŞINA durum göstergesi değildir; her rozet metin de içerir.
// - İkon-only düğme yoktur; tüm etkileşimli öğeler klavye ile erişilebilir
//   standart `button` öğeleridir.
// ============================================================================

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Loader2,
  Inbox,
  AlertTriangle,
  ShieldAlert,
  Clock3,
  RefreshCw,
  ArrowLeft,
} from 'lucide-react';
import { obtLabel, obtTone } from './obtEnums';

// ---------------------------------------------------------------------------
// Başlık
// ---------------------------------------------------------------------------

export function ObtPageHeader({ title, description, actions = null, onBack = null }) {
  return (
    <div className="space-y-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Panele Dön
        </button>
      )}
      {/* Aksiyonlar geniş olduğunda (ör. Aktif/Arşiv/Silinenler + Yenile + Yeni
          Bilgi Talebi) `sm` genişlikte başlık sütunu sıfıra sıkışıp açıklama
          kelime-kelime alt alta düşüyordu. Satır düzenini `lg`'ye ertele; altında
          başlık üstte, aksiyonlar ALTTA (sarmalı) durur. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 lg:flex-1">
          <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">{title}</h2>
          {description && (
            <p className="mt-1 text-sm text-gray-600">{description}</p>
          )}
        </div>
        {actions && <div className="flex flex-wrap gap-2 lg:flex-shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yükleniyor
// ---------------------------------------------------------------------------

export function ObtLoading({ label = 'Yükleniyor…', className }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-3 py-10 text-gray-600',
        className
      )}
    >
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Boş veri
// ---------------------------------------------------------------------------

export function ObtEmptyState({
  title = 'Henüz kayıt bulunmuyor',
  description = null,
  icon: Icon = Inbox,
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <Icon className="h-8 w-8 text-gray-400" aria-hidden="true" />
      <p className="text-sm font-semibold text-gray-700">{title}</p>
      {description && (
        <p className="max-w-md text-sm text-gray-500">{description}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hata
// ---------------------------------------------------------------------------

/**
 * @param {{error: {message: string}|null, onRetry?: () => void}} props
 * `error` daima `normalizeObtError` çıktısıdır; ham backend gövdesi basılmaz.
 */
export function ObtErrorState({ error, onRetry = null }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-3 py-10 text-center"
    >
      <AlertTriangle className="h-8 w-8 text-amber-500" aria-hidden="true" />
      <p className="max-w-md text-sm font-medium text-gray-700">
        {error?.message || 'Beklenmeyen bir hata oluştu.'}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          Tekrar dene
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yetkisiz
// ---------------------------------------------------------------------------

export function ObtUnauthorized({
  message = 'Bu bölüm için yetkiniz bulunmuyor.',
}) {
  return (
    <Card className="border-red-200 bg-red-50">
      <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <ShieldAlert className="h-9 w-9 text-red-500" aria-hidden="true" />
        <div>
          <p className="text-base font-semibold text-red-800">
            Yetkiniz bulunmuyor
          </p>
          <p className="mt-1 max-w-md text-sm text-red-700">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Durum rozeti
// ---------------------------------------------------------------------------

const TONE_CLASSES = Object.freeze({
  neutral: 'bg-gray-100 text-gray-700 ring-gray-300',
  info: 'bg-blue-50 text-blue-700 ring-blue-300',
  warning: 'bg-amber-50 text-amber-800 ring-amber-300',
  success: 'bg-green-50 text-green-700 ring-green-300',
  danger: 'bg-red-50 text-red-700 ring-red-300',
});

/**
 * Teknik enum değerini Türkçe etiketle gösterir.
 * Renk tek başına anlam taşımaz; etiket metni daima görünür.
 */
export function ObtStatusBadge({
  value,
  labelMap,
  fallback = 'Belirsiz',
  label,
  tone,
}) {
  // `label`/`tone` verilirse ham status yerine türetilmiş etiket gösterilir
  // (ör. campaignPhase — "Cevap Toplanıyor" yerine "Müdür kararı bekliyor").
  const t = tone || obtTone(value);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
        TONE_CLASSES[t] || TONE_CLASSES.neutral
      )}
    >
      {label != null ? label : obtLabel(labelMap, value, fallback)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sonraki faz bilgisi
// ---------------------------------------------------------------------------

export const OBT_NEXT_PHASE_TEXT =
  'Bu özellik sonraki frontend fazında kullanıma açılacaktır';

export function ObtPhaseBadge({ phase }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-300">
      <Clock3 className="h-3 w-3" aria-hidden="true" />
      {`Durum: ${phase}'de kullanıma açılacak`}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sayaç kutucuğu — YALNIZ gerçek backend verisiyle kullanılır.
// ---------------------------------------------------------------------------

export function ObtStatTile({
  label,
  value,
  icon: Icon = null,
  tone = 'neutral',
  onClick = null,
}) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={
        clickable
          ? 'cursor-pointer transition hover:border-gray-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400'
          : undefined
      }
      aria-label={clickable ? `${label} — aç` : undefined}
    >
      <CardContent className="flex items-center gap-3 p-4">
        {Icon && (
          <span
            className={cn(
              'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
              TONE_CLASSES[tone] || TONE_CLASSES.neutral
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <p className="text-2xl font-bold leading-none text-gray-900">
            {value}
          </p>
          <p className="mt-1 truncate text-xs font-medium text-gray-600">
            {label}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Görev alanı kartı — tamamlanmamış işlevler aktif gibi GÖSTERİLMEZ.
// ---------------------------------------------------------------------------

export function ObtTaskCard({ title, description, icon: Icon = null, phase = null }) {
  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          {Icon && (
            <Icon className="h-4 w-4 flex-shrink-0 text-green-600" aria-hidden="true" />
          )}
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
        <p className="flex-1 text-sm text-gray-600">{description}</p>
        {phase && (
          <div className="pt-1">
            <ObtPhaseBadge phase={phase} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
